import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Copy, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { dayLabels, fmtH, todayIso } from "../lib/planning";

const selectCls = "mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm";
const labelCls = "text-xs font-semibold uppercase tracking-wide text-slate-700";
const REASONS = [
    { value: "ferie", label: "Ferie" },
    { value: "malattia", label: "Malattia" },
    { value: "permesso", label: "Permesso" },
    { value: "altro", label: "Altro motivo" },
];

/** Copia le assegnazioni di un dipendente su un collega: definitiva oppure sostituzione temporanea con motivo. */
export function CopyAssignmentsDialog({ open, onClose, onSaved }) {
    const [employees, setEmployees] = useState(null);
    const [sourceId, setSourceId] = useState("");
    const [targetId, setTargetId] = useState("");
    const [preview, setPreview] = useState(null);
    const [mode, setMode] = useState("permanent");
    const [replace, setReplace] = useState(false);
    const [dateFrom, setDateFrom] = useState(todayIso());
    const [dateTo, setDateTo] = useState(todayIso());
    const [reason, setReason] = useState("ferie");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSourceId(""); setTargetId(""); setPreview(null); setMode("permanent"); setReplace(false);
        setDateFrom(todayIso()); setDateTo(todayIso()); setReason("ferie"); setNote("");
        api.get("/presenze/planning/employees").then((r) => setEmployees(r.data))
            .catch(() => toast.error("Errore nel caricamento dei dipendenti"));
    }, [open]);

    useEffect(() => {
        if (!sourceId) { setPreview(null); return; }
        api.get("/presenze/assignments", { params: { employee_id: sourceId } })
            .then((r) => setPreview(r.data)).catch(() => setPreview([]));
    }, [sourceId]);

    const source = useMemo(() => employees?.find((e) => e.id === sourceId), [employees, sourceId]);
    const target = useMemo(() => employees?.find((e) => e.id === targetId), [employees, targetId]);
    const previewHours = (preview || []).reduce((s, r) => s + (r.hours_week || 0), 0);
    const overContract = mode === "permanent" && target?.contratto_ore && (replace ? 0 : target.assigned_week_hours) + previewHours > target.contratto_ore;

    const save = async () => {
        if (!sourceId || !targetId) { toast.error("Seleziona dipendente di origine e collega"); return; }
        if (sourceId === targetId) { toast.error("Scegli due dipendenti diversi"); return; }
        if (!preview?.length) { toast.error("Il dipendente di origine non ha assegnazioni"); return; }
        if (mode === "substitution" && (!dateFrom || !dateTo || dateTo < dateFrom)) { toast.error("Indica un periodo valido"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/presenze/assignments/copy", {
                source_employee_id: sourceId, target_employee_id: targetId, mode, replace_existing: replace,
                date_from: mode === "substitution" ? dateFrom : null, date_to: mode === "substitution" ? dateTo : null,
                reason: mode === "substitution" ? reason : null, note: mode === "substitution" ? note : null,
            });
            if (data.mode === "permanent") {
                toast.success(`Copiate ${data.copied} assegnazioni${data.updated ? `, aggiornate ${data.updated}` : ""}${data.removed ? `, rimosse ${data.removed} precedenti` : ""}`);
            } else {
                toast.success(`Sostituzione registrata: ${data.created} turni dal ${new Date(data.date_from).toLocaleDateString("it-IT")} al ${new Date(data.date_to).toLocaleDateString("it-IT")}`);
            }
            onSaved?.(data);
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nella copia");
        } finally { setSaving(false); }
    };

    const empOption = (e) => `${e.cognome} ${e.nome}${e.matricola ? ` (${e.matricola})` : ""} — ${fmtH(e.assigned_week_hours)}h assegnate`;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto" data-testid="copy-assign-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display text-slate-800"><Copy className="h-5 w-5 text-[#376DB3]" /> Copia assegnazioni su un collega</DialogTitle>
                    <DialogDescription>Duplica cantieri, giorni e orari di un dipendente su un altro: in modo definitivo (nuova assunzione) o come sostituzione temporanea (ferie, malattia…).</DialogDescription>
                </DialogHeader>
                {!employees ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto_1fr]">
                            <div>
                                <Label className={labelCls}>Da (dipendente di origine) *</Label>
                                <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} className={selectCls} data-testid="copy-source-select">
                                    <option value="">Seleziona…</option>
                                    {employees.filter((e) => e.assigned_week_hours > 0).map((e) => <option key={e.id} value={e.id}>{empOption(e)}</option>)}
                                </select>
                            </div>
                            <ArrowRight className="mb-2 hidden h-5 w-5 text-slate-400 sm:block" />
                            <div>
                                <Label className={labelCls}>A (collega) *</Label>
                                <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className={selectCls} data-testid="copy-target-select">
                                    <option value="">Seleziona…</option>
                                    {employees.filter((e) => e.id !== sourceId).map((e) => <option key={e.id} value={e.id}>{empOption(e)}</option>)}
                                </select>
                            </div>
                        </div>

                        {sourceId && (
                            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="copy-preview">
                                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    Cosa verrà copiato {preview ? `(${preview.length} assegnazioni · ${fmtH(previewHours)} h/sett.)` : ""}
                                </div>
                                {!preview ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" /> : preview.length === 0 ? (
                                    <div className="text-sm text-amber-700">Il dipendente selezionato non ha assegnazioni.</div>
                                ) : (
                                    <ul className="max-h-40 space-y-1 overflow-y-auto text-xs text-slate-700">
                                        {preview.map((r) => (
                                            <li key={r.id} className="flex flex-wrap items-center gap-x-2" data-testid={`copy-preview-row-${r.id}`}>
                                                <b>{r.worksite_name}</b>{r.client_name && <span className="text-slate-400">{r.client_name}</span>}
                                                <span>· {r.activity_name}</span><span>· {r.days?.length ? dayLabels(r.days) : "secondo date"}</span>
                                                <span className="font-mono-numbers">· {r.start}–{r.end}</span>
                                                {r.frequency_label !== "settimanale" && <span className="text-violet-600">· {r.frequency_label}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        <div>
                            <Label className={labelCls}>Tipo di copia</Label>
                            <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
                                {[
                                    { v: "permanent", t: "Definitiva", d: "Il collega riceve le stesse assegnazioni (nuova assunzione, riorganizzazione). L'origine le mantiene." },
                                    { v: "substitution", t: "Sostituzione temporanea", d: "Per un periodo il collega copre i turni dell'origine, che risulta assente con il motivo indicato." },
                                ].map((o) => (
                                    <button key={o.v} type="button" onClick={() => setMode(o.v)}
                                        className={`rounded-md border p-3 text-left transition-colors ${mode === o.v ? "border-[#376DB3] bg-[#376DB3]/5" : "border-slate-200 hover:bg-slate-50"}`}
                                        data-testid={`copy-mode-${o.v}`}>
                                        <div className="text-sm font-semibold text-slate-800">{o.t}</div>
                                        <div className="mt-0.5 text-[11px] text-slate-500">{o.d}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {mode === "permanent" ? (
                            <label className="flex items-start gap-2 text-sm text-slate-700">
                                <Checkbox checked={replace} onCheckedChange={(v) => setReplace(!!v)} className="mt-0.5" data-testid="copy-replace-checkbox" />
                                <span>Sostituisci le assegnazioni attuali del collega{target?.assigned_week_hours ? ` (oggi ${fmtH(target.assigned_week_hours)} h/sett.)` : ""} invece di aggiungerle</span>
                            </label>
                        ) : (
                            <div className="grid gap-3 sm:grid-cols-4">
                                <div>
                                    <Label className={labelCls}>Dal *</Label>
                                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={selectCls} data-testid="copy-date-from" />
                                </div>
                                <div>
                                    <Label className={labelCls}>Al *</Label>
                                    <input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} className={selectCls} data-testid="copy-date-to" />
                                </div>
                                <div>
                                    <Label className={labelCls}>Motivo assenza *</Label>
                                    <select value={reason} onChange={(e) => setReason(e.target.value)} className={selectCls} data-testid="copy-reason-select">
                                        {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label className={labelCls}>Nota</Label>
                                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Facoltativa" className="mt-1 h-9" data-testid="copy-note-input" />
                                </div>
                                <p className="text-[11px] text-slate-500 sm:col-span-4">
                                    Nel piano settimanale {source ? source.cognome : "l'origine"} risulterà <b>assente ({REASONS.find((r) => r.value === reason)?.label.toLowerCase()})</b> e {target ? target.cognome : "il collega"} comparirà come sostituto; entrambi lo vedono nella propria settimana nel portale.
                                </p>
                            </div>
                        )}

                        {overContract && (
                            <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" data-testid="copy-over-contract-warning">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                Con questa copia {target.cognome} supera le ore contrattuali settimanali ({fmtH((replace ? 0 : target.assigned_week_hours) + previewHours)} h su {fmtH(target.contratto_ore)} h).
                            </div>
                        )}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose} data-testid="copy-cancel-btn">Annulla</Button>
                    <Button onClick={save} disabled={saving || !employees} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="copy-save-btn">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />} {mode === "permanent" ? "Copia assegnazioni" : "Registra sostituzione"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
